import React from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { Card, Chip, Grid, MenuItem, Tab, Tabs, TextField } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import colors from "styles/tokens/colors";
import { DomainStatusChip } from "./components/DomainStatusStepper";
import DesignTab from "./tabs/DesignTab";
import DomainTab from "./tabs/DomainTab";
import ContentTab from "./tabs/ContentTab";
import PackagesTab from "./tabs/PackagesTab";
import ListingsTab from "./tabs/ListingsTab";
import { siteHostname, sitePublicUrl } from "./utils/api";
import { isRealEstateSite, publishedCount } from "./utils/vertical";

const BASE_TABS = [
  { id: "overview", label: "Overview" },
  { id: "design", label: "Design" },
  { id: "domain", label: "Domain" },
  { id: "content", label: "Content" },
];

// The last tab is what the site publishes: trip packages or CRM listings.
function tabsFor(realEstate) {
  return [...BASE_TABS, realEstate ? { id: "listings", label: "Listings" } : { id: "packages", label: "Packages" }];
}

function StatusCard({ label, children, caption }) {
  return (
    <Card sx={{ p: 2, height: "100%", border: `1px solid ${colors.border.default}`, boxShadow: "none" }}>
      <MDTypography variant="caption" color="text" fontWeight="medium" textTransform="uppercase" letterSpacing={0.5}>{label}</MDTypography>
      <MDBox mt={0.75}>{children}</MDBox>
      {caption && <MDTypography variant="caption" color="text" display="block" mt={0.5} sx={{ wordBreak: "break-all" }}>{caption}</MDTypography>}
    </Card>
  );
}

StatusCard.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node.isRequired, caption: PropTypes.node };
StatusCard.defaultProps = { caption: null };

function toneChip(label, tone) {
  const palette = colors.status[tone];
  return <Chip size="small" label={label} sx={{ bgcolor: palette.background, color: palette.text, fontWeight: 600 }} />;
}

function NextStep({ done, title, detail, action }) {
  return (
    <MDBox display="flex" alignItems="center" gap={1.5} py={1.25} sx={{ borderBottom: `1px solid ${colors.border.default}`, "&:last-of-type": { borderBottom: 0 } }}>
      {done
        ? <CheckCircleIcon sx={{ color: colors.success.main }} />
        : <RadioButtonUncheckedIcon sx={{ color: colors.text.light }} />}
      <MDBox flex={1}>
        <MDTypography variant="button" fontWeight="medium" display="block">{title}</MDTypography>
        <MDTypography variant="caption" color="text">{detail}</MDTypography>
      </MDBox>
      {action}
    </MDBox>
  );
}

NextStep.propTypes = { done: PropTypes.bool.isRequired, title: PropTypes.string.isRequired, detail: PropTypes.string.isRequired, action: PropTypes.node };
NextStep.defaultProps = { action: null };

function hasPublishedHome(builderPages) {
  const home = (builderPages || []).find((page) => page.path === "/");
  return Boolean(home && (home.published_revision > 0 || home.published?.content?.length));
}

function WebsiteOverview({ state, onCreateAnother, onOpenBuilder }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { site, sites, templates, domain, catalogue, listings, builderPages, saving } = state;
  const realEstate = isRealEstateSite(site);
  const TABS = tabsFor(realEstate);
  const tab = TABS.some((item) => item.id === searchParams.get("tab")) ? searchParams.get("tab") : "overview";

  const goTo = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "overview") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params);
  };

  const published = site.status === "published";
  const publicUrl = sitePublicUrl(site, domain);
  const platformHost = siteHostname(site, domain);
  const domainStatus = domain?.domain ? domain.status : "none";
  const homePublished = hasPublishedHome(builderPages);
  const packageCount = catalogue.filter((entry) => entry.is_published).length;
  const listingCount = publishedCount(listings);
  const template = templates.find((item) => item.id === site.template_id);
  const layout = template?.layouts?.find((item) => item.id === site.layout_id);

  return (
    <MDBox>
      <Card sx={{ p: 2, mb: 3, border: `1px solid ${colors.border.default}` }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField select fullWidth label="Website" value={site.id} onChange={(event) => state.selectSite(event.target.value)}>
              {sites.map((item) => (
                <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={7}>
            <MDBox display="flex" gap={1} justifyContent={{ xs: "flex-start", md: "flex-end" }} flexWrap="wrap">
              <MDButton color={published ? "dark" : "success"} variant={published ? "outlined" : "contained"} disabled={saving} onClick={() => state.setPublished(!published)}>
                {published ? "Unpublish" : "Publish"}
              </MDButton>
              <MDButton color="info" onClick={onOpenBuilder}>Open builder</MDButton>
              <MDButton color="info" variant="outlined" disabled={!publicUrl} onClick={() => window.open(publicUrl, "_blank", "noopener,noreferrer")}>
                Open site <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} />
              </MDButton>
              <MDButton color="dark" variant="text" onClick={onCreateAnother}>Create another website</MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      <MDBox sx={{ borderBottom: `1px solid ${colors.border.default}`, mb: 3 }}>
        <Tabs
          value={tab}
          onChange={(event, value) => goTo(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ background: "transparent", "& .MuiTab-root": { flex: "0 0 auto", px: 2, minWidth: 0 } }}
        >
          {TABS.map((item) => <Tab key={item.id} value={item.id} label={item.label} />)}
        </Tabs>
      </MDBox>

      {tab === "overview" && (
        <>
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} lg>
              <StatusCard label="Status" caption={`Revision ${site.revision}`}>
                {published ? toneChip("Published", "success") : toneChip("Draft", "neutral")}
              </StatusCard>
            </Grid>
            <Grid item xs={12} sm={6} lg>
              <StatusCard label="PearlCRM address" caption={platformHost || "Assigned when the website is saved"}>
                {platformHost
                  ? <MDButton size="small" variant="text" color="info" sx={{ p: 0, minHeight: 0 }} onClick={() => window.open(`https://${platformHost}`, "_blank", "noopener,noreferrer")}>Open <OpenInNewIcon fontSize="inherit" sx={{ ml: 0.5 }} /></MDButton>
                  : toneChip("Pending", "neutral")}
              </StatusCard>
            </Grid>
            <Grid item xs={12} sm={6} lg>
              <StatusCard label="Custom domain" caption={domain?.domain || "None connected"}>
                <DomainStatusChip status={domainStatus} />
              </StatusCard>
            </Grid>
            <Grid item xs={12} sm={6} lg>
              {realEstate ? (
                // The property home is built from listings; a published builder
                // home page replaces it (storefront app/page.tsx).
                <StatusCard label="Home page" caption={homePublished ? "A builder page replaces the property home page" : "Built from your published listings"}>
                  {homePublished ? toneChip("Builder page", "warning") : toneChip("Property home", "success")}
                </StatusCard>
              ) : (
                <StatusCard label="Builder home page" caption={builderPages === null ? "Couldn't load builder pages" : null}>
                  {homePublished ? toneChip("Published", "success") : toneChip("Using template", "info")}
                </StatusCard>
              )}
            </Grid>
            <Grid item xs={12} sm={6} lg>
              <StatusCard label={realEstate ? "Listings" : "Packages"} caption="Published on this website">
                <MDTypography variant="h5">{realEstate ? listingCount : packageCount}</MDTypography>
              </StatusCard>
            </Grid>
          </Grid>

          <Card sx={{ p: { xs: 2, md: 3 }, border: `1px solid ${colors.border.default}` }}>
            <MDTypography variant="h6" mb={1}>Get this website live</MDTypography>
            <NextStep
              done={Boolean(site.layout_id)}
              title="Choose a design"
              detail={layout ? `${template?.name || "Template"} · ${layout.name}` : "Pick a template and layout."}
              action={<MDButton size="small" variant="text" color="info" onClick={() => goTo("design")}>Change</MDButton>}
            />
            {realEstate ? (
              <NextStep
                done={Boolean(site.settings?.site_editor?.home?.title)}
                title="Word your home page"
                detail="Set the headline, introduction and contact line visitors see first."
                action={<MDButton size="small" variant="text" color="info" onClick={() => goTo("content")}>Edit</MDButton>}
              />
            ) : (
              <NextStep
                done={homePublished}
                title="Build your home page"
                detail={homePublished ? "Published from the storefront builder." : "Start from your layout and make it yours in the builder."}
                action={<MDButton size="small" variant="text" color="info" onClick={onOpenBuilder}>Open builder</MDButton>}
              />
            )}
            {realEstate ? (
              <NextStep
                done={listingCount > 0}
                title="Publish listings"
                detail={listingCount ? `${listingCount} listing${listingCount === 1 ? "" : "s"} on the website.` : "Choose which of your listings appear on the website."}
                action={<MDButton size="small" variant="text" color="info" onClick={() => goTo("listings")}>{listingCount ? "Manage" : "Choose"}</MDButton>}
              />
            ) : (
              <NextStep
                done={packageCount > 0}
                title="Publish trip packages"
                detail={packageCount ? `${packageCount} package${packageCount === 1 ? "" : "s"} on the website.` : "Open a Trip Package and select Publish to website."}
                action={<MDButton size="small" variant="text" color="info" onClick={() => goTo("packages")}>View</MDButton>}
              />
            )}
            <NextStep
              done={domainStatus === "active"}
              title="Connect your domain (optional)"
              detail={domain?.domain ? `${domain.domain}` : `Visitors can already use ${platformHost || "your PearlCRM address"}.`}
              action={<MDButton size="small" variant="text" color="info" onClick={() => goTo("domain")}>{domain?.domain ? "Manage" : "Connect"}</MDButton>}
            />
            <NextStep
              done={published}
              title="Publish the website"
              detail={published ? "Visitors can see this website." : "Draft websites are only visible in preview."}
              action={!published ? <MDButton size="small" variant="text" color="success" disabled={saving} onClick={() => state.setPublished(true)}>Publish</MDButton> : null}
            />
          </Card>
        </>
      )}

      {tab === "design" && (
        <DesignTab site={site} templates={templates} saving={saving} patch={state.patch} previewUrl={state.previewUrl} hasBuilderPage={homePublished} />
      )}
      {tab === "domain" && (
        <DomainTab
          site={site}
          domain={domain}
          saving={saving}
          setDomain={state.setDomain}
          verifyDomain={state.verifyDomain}
          removeDomain={state.removeDomain}
          refreshDomain={state.refreshDomain}
          patch={state.patch}
          checkSlug={state.checkSlug}
        />
      )}
      {tab === "content" && (
        <ContentTab site={site} saving={saving} patch={state.patch} onOpenBuilder={onOpenBuilder} onError={state.setError} realEstate={realEstate} />
      )}
      {tab === "packages" && <PackagesTab catalogue={catalogue} />}
      {tab === "listings" && <ListingsTab listings={listings} saving={saving} setListingEntry={state.setListingEntry} />}
    </MDBox>
  );
}

WebsiteOverview.propTypes = {
  state: PropTypes.shape({
    site: PropTypes.object.isRequired,
    sites: PropTypes.arrayOf(PropTypes.object).isRequired,
    templates: PropTypes.arrayOf(PropTypes.object).isRequired,
    domain: PropTypes.object,
    catalogue: PropTypes.arrayOf(PropTypes.object).isRequired,
    listings: PropTypes.arrayOf(PropTypes.object).isRequired,
    setListingEntry: PropTypes.func.isRequired,
    builderPages: PropTypes.arrayOf(PropTypes.object),
    saving: PropTypes.bool.isRequired,
    selectSite: PropTypes.func.isRequired,
    setPublished: PropTypes.func.isRequired,
    patch: PropTypes.func.isRequired,
    previewUrl: PropTypes.func.isRequired,
    setDomain: PropTypes.func.isRequired,
    verifyDomain: PropTypes.func.isRequired,
    removeDomain: PropTypes.func.isRequired,
    refreshDomain: PropTypes.func.isRequired,
    checkSlug: PropTypes.func.isRequired,
    setError: PropTypes.func.isRequired,
  }).isRequired,
  onCreateAnother: PropTypes.func.isRequired,
  onOpenBuilder: PropTypes.func.isRequired,
};

export default WebsiteOverview;
